import {
  applyDocumentTheme,
  getStoredThemePreference,
  resolveTheme,
  type ResolvedTheme,
} from "./theme";

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface TelegramBackButton {
  show: () => void;
  hide: () => void;
  onClick: (callback: () => void) => void;
  offClick: (callback: () => void) => void;
}

interface TelegramHapticFeedback {
  impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
  notificationOccurred: (type: "error" | "success" | "warning") => void;
}

interface TelegramDownloadFileParams {
  url: string;
  file_name: string;
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  colorScheme?: "light" | "dark";
  initData?: string;
  initDataUnsafe?: {
    user?: TelegramUser;
    start_param?: string;
  };
  BackButton?: TelegramBackButton;
  HapticFeedback?: TelegramHapticFeedback;
  isVersionAtLeast?: (version: string) => boolean;
  downloadFile?: (params: TelegramDownloadFileParams, callback?: (accepted: boolean) => void) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent?: (event: "themeChanged", callback: () => void) => void;
  offEvent?: (event: "themeChanged", callback: () => void) => void;
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

export function getTelegramWebApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

export async function loadTelegramSdk(): Promise<void> {
  const launchContext = `${window.location.search}${window.location.hash}`;
  const isTelegramLaunch = /tgWebApp(?:Version|Platform|Data)=/i.test(launchContext) || "TelegramWebviewProxy" in window;

  if (!isTelegramLaunch || getTelegramWebApp()) return;

  await new Promise<void>((resolve) => {
    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-web-app.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.append(script);
  });
}

export function initializeTelegram(): void {
  const webApp = getTelegramWebApp();
  const theme = resolveTheme(getStoredThemePreference(), getPreferredColorScheme());

  applyDocumentTheme(theme);

  if (!webApp) return;

  webApp.ready();
  webApp.expand();
  syncTelegramThemeColors(theme);
}

export function getPreferredColorScheme(): ResolvedTheme {
  return getTelegramWebApp()?.colorScheme ?? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
}

export function syncTelegramThemeColors(theme: ResolvedTheme): void {
  const webApp = getTelegramWebApp();
  if (!webApp) return;

  if (webApp.isVersionAtLeast?.("6.1")) {
    webApp.setHeaderColor?.(theme === "dark" ? "#151616" : "#e70519");
    webApp.setBackgroundColor?.(theme === "dark" ? "#121313" : "#f5f7f8");
  }
}

export function getTelegramUser(): TelegramUser | undefined {
  return getTelegramWebApp()?.initDataUnsafe?.user;
}

export function triggerHaptic(kind: "copy" | "open" | "warning" = "open"): void {
  const haptic = getTelegramWebApp()?.HapticFeedback;
  if (!haptic) return;

  if (kind === "copy") haptic.notificationOccurred("success");
  else if (kind === "warning") haptic.notificationOccurred("warning");
  else haptic.impactOccurred("light");
}

export function requestTelegramFileDownload(url: string, fileName: string): boolean {
  const webApp = getTelegramWebApp();
  if (!webApp) return false;

  const absoluteUrl = new URL(url, window.location.origin).href;

  if (webApp.downloadFile && webApp.isVersionAtLeast?.("8.0")) {
    try {
      webApp.downloadFile({ url: absoluteUrl, file_name: fileName });
      triggerHaptic("open");
      return true;
    } catch {
      // Fall through to the external-browser option for older clients.
    }
  }

  if (webApp.openLink) {
    webApp.openLink(absoluteUrl, { try_instant_view: false });
    triggerHaptic("open");
    return true;
  }

  return false;
}

export function bindTelegramBackButton(callback: () => void): () => void {
  const backButton = getTelegramWebApp()?.BackButton;
  if (!backButton) return () => undefined;

  backButton.show();
  backButton.onClick(callback);
  return () => {
    backButton.offClick(callback);
    backButton.hide();
  };
}
